import { getActiveMarketingTheme } from "@/services/user/recommendation/data";
import UserApiBase from "@/services/base/UserApiBase";
import { PaginatedResponse } from "@/schemas/api/pagination";
import { AppUserSession } from "@/schemas/auth/session";
import {
  CategoryRecommendationRequest,
  DeprecatedRecommendationCategories,
  RecommendationCategory,
  RecommendationCategoryFeed,
  UserRecommendationJobResponse,
  UserRecommendationJobResponseSchema,
  UserRecommendedCategoryFeedResponse,
  UserRecommendedCategoryFeedResponseSchema,
  UserRecommendedCategoryResponse,
  UserRecommendedCategoryResponseSchema,
  UserRecommendedItem,
} from "@/schemas/user/explore";

class RecommendationService extends UserApiBase {
  public constructor(session: AppUserSession) {
    super("/recommendation", session);
  }

  public async getRecommendationJobStatus(
    jobId: string,
  ): Promise<UserRecommendationJobResponse> {
    const response = await this.get(`/job/${jobId}`);
    const validatedResponse =
      UserRecommendationJobResponseSchema.parse(response);
    return validatedResponse;
  }

  public async getCategoryRecommendations(
    request: CategoryRecommendationRequest,
    isDev?: boolean,
  ): Promise<UserRecommendedCategoryResponse> {
    let response;

    if (isDev) {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      response = this.getMockCategoryRecommendations(request);
    } else {
      response = await this.post(`/`, request);
    }

    const validatedResponse =
      UserRecommendedCategoryResponseSchema.parse(response);
    return validatedResponse;
  }

  public async getPreviewRecommendations(
    isDev?: boolean,
  ): Promise<UserRecommendedCategoryFeedResponse> {
    let response;

    if (isDev) {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      response = this.getMockPreviewRecommendations();
    } else {
      response = await this.post(`/category_preview`, {});
    }

    const validatedResponse =
      UserRecommendedCategoryFeedResponseSchema.parse(response);

    return validatedResponse;
  }

  public async refreshRecommendationJob(): Promise<UserRecommendationJobResponse> {
    const response = await this.post<UserRecommendationJobResponse>(
      `/refresh_recommendation_job`,
      {},
    );
    const validatedResponse =
      UserRecommendationJobResponseSchema.parse(response);

    return validatedResponse;
  }

  // Mock data methods
  private getMockCategoryRecommendations(
    request: CategoryRecommendationRequest,
  ): UserRecommendedCategoryResponse {
    const { items: allItems } = getActiveMarketingTheme();

    // Calculate pagination
    const limit = request.page?.limit || 10;
    const startIndex = request.page?.next ? parseInt(request.page.next) : 0;
    const endIndex = startIndex + limit;

    // Slice the items based on pagination parameters
    const items = allItems.slice(startIndex, endIndex);

    // Calculate next token
    const next = endIndex < allItems.length ? endIndex.toString() : null;

    // Create paginated response
    const paginatedResponse: PaginatedResponse<UserRecommendedItem> = {
      items,
      next,
      total: allItems.length,
    };

    return {
      jobId: "marketing-job-id",
      items: paginatedResponse,
    };
  }

  private getMockPreviewRecommendations(): {
    jobId: string;
    nextRefreshTime: string;
    categories: RecommendationCategoryFeed[];
  } {
    const jobId = "marketing-job-id";
    const categories = Object.values(RecommendationCategory).filter(
      (category) =>
        !Object.values(DeprecatedRecommendationCategories).includes(category),
    );

    const activeTheme = getActiveMarketingTheme();
    const allProfiles = activeTheme.items;
    const profilesPerCategory = 12; // should be enough to fill 2 screens

    // Distribute different profiles to each category using metadata
    const items: RecommendationCategoryFeed[] = categories.map(
      (category, index) => {
        const categoryProfiles: UserRecommendedItem[] = [];

        // Get profiles that have priority for this category
        const prioritizedProfiles = activeTheme.categoryPriorities
          .flatMap((p) => {
            const categoryPriority = p.categories.find(
              (c) => c.category === category,
            );
            if (!categoryPriority) return [];
            return {
              profileId: p.profileId,
              priority: categoryPriority.priority,
            };
          })
          .sort((a, b) => a.priority - b.priority); // Sort by priority (lower = higher priority)

        // Add priority profiles first
        const priorityProfileIds = prioritizedProfiles.map((p) => p.profileId);
        const priorityProfileItems = priorityProfileIds
          .map((id) => allProfiles.find((p) => p.id === id))
          .filter((p): p is UserRecommendedItem => p !== undefined);

        categoryProfiles.push(...priorityProfileItems);

        // Fill remaining slots with other profiles (excluding already added ones)
        const remainingProfiles = allProfiles.filter(
          (p) => !priorityProfileIds.includes(p.id),
        );

        // Use offset for variety in non-priority profiles
        const offset = (index * 2) % remainingProfiles.length;
        const rotatedRemaining = [
          ...remainingProfiles.slice(offset),
          ...remainingProfiles.slice(0, offset),
        ];

        const slotsRemaining = profilesPerCategory - categoryProfiles.length;
        for (let i = 0; i < slotsRemaining; i++) {
          const profile = rotatedRemaining[i % rotatedRemaining.length];
          if (profile) {
            categoryProfiles.push(profile);
          }
        }

        return {
          jobId,
          category,
          rank: index,
          items: {
            items: categoryProfiles,
            next: null,
            total: categoryProfiles.length,
          },
        };
      },
    );

    return {
      jobId,
      nextRefreshTime: new Date(Date.now() + 15 * 60 * 1000).toISOString(), // 15 minutes from now
      categories: items,
    };
  }
}

export default RecommendationService;
